/**
 * Minimal core package - only essential functionality
 * filepath: /home/janrau/tm-integration/packages/core/src/index.ts
 */

// Core engine
export { LionRapidBuilder } from './builder.js';
export { LionRapidCore } from './lionRapid.js';

// Base infrastructure
export { EventEmitter } from './base/event-emitter.js';
export { HandlerChain } from './handlers/handler-chain.js';
export { MemoryRepository, NetworkRepository } from './repositories/index.js';
export { StaticRepository } from './repositories/static-repository.js';
export type { StaticBundle } from './repositories/static-repository.js';

// Sync utilities
export { MissingTranslationQueue } from './sync/missing-translation-queue.js';
export type {
  MissingTranslationItem,
  MissingTranslationQueueConfig,
} from './sync/missing-translation-queue.js';

// Plugin system
export type {
  FormatterFunction,
  IAsyncHandler,
  IEmbeddedPlugin,
  IEventPlugin,
  IPlugin,
  ISyncHandler,
  PluginContext,
} from './interfaces/index.js';

// Repository interfaces (for external storage implementations)
export type {
  ISyncRepository,
  IAsyncRepository,
  IStatsProvider,
} from './interfaces/repository.interface.js';

// Core types
export type { TranslationRequest, TranslationResult } from './types/index.js';

// Content module (Unified Content Architecture)
export type {
  ContentUnit,
  HtmlBlock,
  IParser,
  ISerializer,
  IValidator,
  SplitBlocksResult,
  ValidationError,
  ValidationErrorCode,
  ValidationResult,
  ValidationWarning,
  ValidationWarningCode,
} from './content/index.js';

export {
  ContentValidator,
  HTMLSerializer,
  MarkupParser,
  // Raw-tag helpers — used by react JSX parser/serializer and any other
  // consumer that needs to produce or interpret `raw:<...>` styles.
  VOID_TAGS,
  serializeOpeningTag,
  extractTagName,
  readRawStyle,
  // Shared allowlist HTML sanitizer — the single implementation for every
  // innerHTML sink (core serializer/editor/snapshot, react <Trans>, WP TS);
  // the WP PHP port mirrors SAFE_TAGS/SAFE_ATTRS.
  SAFE_TAGS,
  SAFE_ATTRS,
  SAFE_STYLE_PROPS,
  escapeHtml,
  sanitizeUrl,
  sanitizeStyle,
  sanitizeOpeningTag,
  sanitizeContentUnit,
  // Shared string-level block scanner (lifted from the edge proxy) — the one
  // splitter every non-DOM surface (edge, webhook) uses to find translatable
  // block boundaries. Structural only; callers key/filter themselves.
  splitBlocks,
  replaceBlockInners,
} from './content/index.js';

// auto_ key generation (Node-safe: pure-JS md5 over UTF-8 bytes, no DOM).
// Re-exported from the browser entry so non-DOM consumers — notably
// @lionrapid/cli — can import it from the package root rather than a deep
// `@lionrapid/core/browser/md5` path. Same function the edge worker, WordPress
// PHP/TS, and browser runtime all use; covered by parity-vectors.json.
export { generateAutoKey, md5 } from './browser/md5.js';

// Translation exclude filter (Node-safe: pure regex, no DOM). Re-exported so
// the CLI source scanner skips exactly the same emails/URLs/numbers/phones the
// DOM auto-detect path skips — byte-parity on what becomes a translatable key.
export { ExcludeFilter } from './browser/exclude-filter.js';
export type { ExcludePatterns } from './browser/exclude-filter.js';

// DOM-tree sanitizer (browser-only: uses live-DOM APIs). Consumed by the
// snapshot viewer and by react `<Trans>` as a defense-in-depth pass over
// tHtml output. Callers must guard `typeof document !== 'undefined'`.
export { sanitizeElement, sanitizeStyleSheetText } from './browser/sanitizer.js';

// BCP-47 locale utilities (Node-safe, pure). Matching mirrors @lionrapid/edge's
// resolveLocale (incl. the ISO 639-2 table, which all three surfaces share);
// the tag grammar is deliberately broader than edge's — see locale.ts. RTL set
// mirrors tm-server. Consumers: browser auto-init, and any package needing to
// negotiate/normalize locales identically.
export {
  normalizeLocale,
  matchLocale,
  parseAcceptLanguage,
  localeDir,
  primarySubtag,
  localeCandidates,
  ISO639_2_TO_1,
  LOCALE_TAG_REGEX,
} from './utils/locale.js';

// Cache module (TanStack Query-inspired)
export { CacheManager } from './cache/index.js';
export type {
  CacheEventPayloads,
  CacheManagerEntry,
  InvalidateOptions,
} from './cache/index.js';

// Error classes
export {
  isClientError,
  isRetryableError,
  isTranslationError,
  NetworkError,
  ServerError,
  TranslationError,
  TranslationLockedError,
  TranslationNotFoundError,
} from './errors/translation-errors.js';

// Type safety enhancements
export type {
  FormatterParams,
  ProcessorParams,
  TranslationParams,
} from './types/parameters.types.js';

export type {
  ContentUnitData,
  FlatTranslationData,
  NamespaceData,
  TranslationData,
  TranslationResponse,
  TranslationValue,
} from './types/translation-data.types.js';

export type {
  GenericEventPayload,
  HandlerAfterPayload,
  HandlerBeforePayload,
  HandlerEventData,
} from './types/event-payloads.types.js';

export type {
  LanguageChangePayload,
  LionRapidContext,
  LionRapidEventEnvelope,
  LionRapidEventPayloads,
} from './types/lionRapid.types.js';

export type {
  CacheEntries,
  CacheEntry,
  CacheStats,
  HandlerChainInfo,
  HandlerInfo,
} from './types/cache.types.js';

// Generated types support
export type {
  RequiredParamKeys,
  // Core interfaces
  TranslationKeys,

  // Union type helpers
  ZeroParamKeys,
} from './types/generated.types.js';

// Note: Branded type guards were removed as they're not used in the current implementation

// Browser storage repositories
export { LocalStorageRepository } from './browser/storage/localstorage-repository.js';
export type { LocalStorageRepositoryConfig } from './browser/storage/localstorage-repository.js';

export { CacheStorageRepository } from './browser/storage/cache-storage-repository.js';
export type { CacheStorageRepositoryConfig } from './browser/storage/cache-storage-repository.js';

// The Cache API names/prefix core owns. Exported so a package sharing the
// origin (notably @lionrapid/storage's Service Worker) can assert it stays out
// of core's caches instead of re-declaring the literals and drifting.
export { CORE_CACHE_NAMES, CORE_CACHE_ENTRY_PREFIX } from './browser/storage/cache-names.js';

// Essential utilities
export {
  parseKeyPath,
  prepareTranslationRequest,
  resolveNestedKey,
} from './utils/index.js';

// Internal utilities (for package development)
export {
  Colors,
  createPackageLogger,
  Logger,
  logger,
  measure,
} from './utils/logger.js';

export { LoggerConfig, LogLevel } from './config/logger.config.js';

// The API host the SDK defaults to. Exported so the value has one home and
// the guard test can assert it — see config/api.config.ts.
export { DEFAULT_API_BASE_URL } from './config/api.config.js';
export type { CallSite, PackageLoggerConfig } from './utils/logger.js';

// Repository configuration types
export type {
  MemoryRepositoryConfig,
  NetworkRepositoryConfig,
  PreloadNamespaces,
  ProjectConfig,
  StorageRepositoryConfig,
  SyncMissingConfig,
} from './config/repository.config.js';

// Version information
export const VERSION = '0.6.6';
